import React, { useEffect, useState } from "react";
import { RedeemCodes } from "@apis/index";
import { toast } from "react-toastify";
import Button from "@components/button";
import { useModal } from "@context/ModalContext";
import { API_KEY } from "@utils/request";
import { Helper } from "@utils/index";

interface ExportProgressModalProps {
  jobId: string;
}

const ExportProgressModal: React.FC<ExportProgressModalProps> = ({ jobId }) => {
  const { hideModal } = useModal();
  const [status, setStatus] = useState<any>(null);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isDownloadingFile, setIsDownloadingFile] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!jobId) return;

    let isPolling = true;

    const poll = async () => {
      if (!isPolling) return;
      try {
        const res: any = await RedeemCodes.checkExportStatus(jobId);
        setStatus(res);

        if (res.state === "completed") {
          isPolling = false;
          handleFinalDownload();
        } else if (res.state === "failed") {
          isPolling = false;
          setErrorMsg("Export generation failed on the server.");
          toast.error("Export failed on server");
        } else {
          setTimeout(poll, 2000);
        }
      } catch (error: any) {
        console.error(error);
        setTimeout(poll, 3000);
      }
    };

    const handleFinalDownload = async () => {
      try {
        setIsDownloadingFile(true);
        const downloadUrl = RedeemCodes.getDownloadUrl(jobId);
        const user = Helper.getUser();
        const token = user?.token;

        const response = await fetch(downloadUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Api-Key": API_KEY || "",
          },
        });

        if (!response.ok) throw new Error("Network response was not ok");

        const contentLength = response.headers.get("content-length");
        const total = contentLength ? parseInt(contentLength, 10) : 0;
        let loaded = 0;

        const reader = response.body?.getReader();
        if (!reader) throw new Error("Failed to get reader");

        const chunks = [];
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          loaded += value.length;
          if (total > 0) {
            setDownloadProgress(Math.round((loaded / total) * 100));
          }
        }

        const blob = new Blob(chunks, {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", `redeem_codes_${Date.now()}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);

        setIsDownloadingFile(false);
        toast.success("Download completed successfully!");
        
        // Short delay to show 100% completion before closing
        setTimeout(() => {
          hideModal();
        }, 1500);
      } catch (error: any) {
        setIsDownloadingFile(false);
        setErrorMsg("Failed to download file to device.");
        toast.error("Failed to download file to device");
        console.error(error);
      }
    };

    poll();
    return () => {
      isPolling = false;
    };
  }, [jobId]);

  const progress = isDownloadingFile ? downloadProgress : status?.progress || 0;
  const isCompleted = status?.state === "completed" && !isDownloadingFile;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px 10px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          width: "70px",
          height: "70px",
          marginBottom: "20px",
          background: errorMsg ? "#fef2f2" : "#f8f9fa",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "32px",
          boxShadow: "inset 0 2px 4px rgba(0,0,0,0.02)",
        }}
      >
        {errorMsg ? "❌" : isCompleted ? "✨" : isDownloadingFile ? "📥" : "⏳"}
      </div>

      <h3
        style={{
          fontSize: "1.25rem",
          fontWeight: 600,
          color: "#1e293b",
          marginBottom: "10px",
        }}
      >
        {errorMsg
          ? "Export Failed"
          : isDownloadingFile
            ? "Downloading File..."
            : isCompleted
              ? "Download Started!"
              : "Generating Excel File..."}
      </h3>

      <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "25px", maxWidth: "340px" }}>
        {errorMsg
          ? errorMsg
          : isDownloadingFile
            ? "We are streaming the Excel sheet directly to your computer."
            : isCompleted
              ? "File ready! Your download has started automatically."
              : `Processing redeem codes batch. Please do not close this modal.`}
      </p>

      {!errorMsg && (
        <div style={{ width: "100%", maxWidth: "360px", marginBottom: "25px" }}>
          <div
            style={{
              width: "100%",
              height: "8px",
              background: "#e2e8f0",
              borderRadius: "4px",
              overflow: "hidden",
              marginBottom: "8px",
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: "100%",
                background: isDownloadingFile
                  ? "linear-gradient(90deg, #10b981, #059669)"
                  : "linear-gradient(90deg, #6366f1, #4f46e5)",
                transition: "width 0.3s ease",
                borderRadius: "4px",
              }}
            />
          </div>
          <span style={{ fontSize: "12px", fontWeight: "600", color: "#475569" }}>
            {isDownloadingFile ? "Download" : "Server Generation"}: {progress}%
          </span>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "center", gap: "10px", width: "100%" }}>
        <Button
          title={errorMsg ? "Close" : "Cancel"}
          type="button"
          onClick={hideModal}
          disabled={isDownloadingFile}
          styleButton={
            errorMsg
              ? "background: #ef4444; color: #fff; padding: 10px 30px;"
              : "background: #f1f5f9; color: #475569; padding: 10px 30px;"
          }
        />
      </div>
    </div>
  );
};

export default ExportProgressModal;
